/**
 * TabsRenderer - Renders Tabs containers with multiple panels.
 *
 * Each tab panel can contain its own set of child elements.
 * Children are filtered by parentPanelId to show only elements for the active tab.
 *
 * Ported from the original MyVault application.
 */

import React, { useState } from "react";
import { Box, Tabs, Tab, TabList, TabPanel } from "@mui/joy";
import type {
  CanvasElement,
  ContainerElementConfig,
  ContainerPanel,
} from "../../../types";
import { ChildrenRenderer } from "../ChildrenRenderer";
import { ElementRenderer } from "../ElementRenderer";
import type { FieldState } from "../CanvasRenderer";
import { tabPresentation } from "./tabPresentation";

/**
 * Props for TabsRenderer
 */
interface TabsRendererProps {
  /** The tabs container element */
  element: CanvasElement;
  /** All canvas elements (for child lookup) */
  allElements: CanvasElement[];
  /** Mode: edit or runtime */
  mode: "edit" | "runtime";
  /** Field state for form values */
  fieldState?: Record<string, FieldState>;
}

/**
 * Default panels if none configured
 */
const DEFAULT_PANELS: ContainerPanel[] = [
  { id: "tab-1", label: "Tab 1" },
  { id: "tab-2", label: "Tab 2" },
];

/**
 * TabsRenderer - Renders a tabbed container.
 */
export function TabsRenderer({
  element,
  allElements,
  mode,
  fieldState = {},
}: TabsRendererProps) {
  const config = element.props as unknown as ContainerElementConfig;
  const panels = config?.panels?.length ? config.panels : DEFAULT_PANELS;
  const defaultPanelId = config?.defaultPanelId || panels[0]?.id;
  const presentation = tabPresentation(config?.tabStyle);

  const [activeTab, setActiveTab] = useState<string>(defaultPanelId);

  // Render function for children
  const renderElement = (child: CanvasElement, isNested: boolean) => (
    <ElementRenderer
      key={child.id}
      element={child}
      allElements={allElements}
      isNested={isNested}
      mode={mode}
      fieldState={fieldState}
    />
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "md",
        overflow: "hidden",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value as string)}
        sx={presentation?.tabs ?? { bgcolor: "background.level1" }}
      >
        <TabList sx={presentation?.list}>
          {panels.map((panel) => (
            <Tab
              key={panel.id}
              value={panel.id}
              indicatorPlacement={presentation ? "bottom" : undefined}
              sx={presentation?.tab}
            >
              {panel.label}
            </Tab>
          ))}
        </TabList>

        {panels.map((panel) => (
          <TabPanel
            key={panel.id}
            value={panel.id}
            sx={{
              p: 2,
              flexGrow: 1,
              overflow: "auto",
            }}
          >
            <ChildrenRenderer
              elements={allElements}
              parentId={element.id}
              panelId={panel.id}
              columns={config?.internalGrid?.columns}
              gap={config?.internalGrid?.gap}
              rowHeight={config?.internalGrid?.rowHeight}
              internalGrid={config?.internalGrid}
              mode={mode}
              fieldState={fieldState}
              renderElement={renderElement}
              showEmptyPlaceholder={mode === "edit"}
              emptyPlaceholderText={
                mode === "edit"
                  ? `Drop elements into "${panel.label}"`
                  : undefined
              }
            />
          </TabPanel>
        ))}
      </Tabs>
    </Box>
  );
}

export default TabsRenderer;
